// theme.js
import { createTheme } from '@mui/material/styles';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
const shadows = Array(25).fill('none');
shadows[1] = '0 1px 12px rgba(0, 65, 144, 0.05)';
shadows[2] = '0 2px 16px rgba(0, 65, 144, 0.08)';
shadows[3] = '0 3px 20px rgba(0, 65, 144, 0.1)';
shadows[4] = '0 4px 24px rgba(0, 65, 144, 0.1)';

const theme = createTheme({
    palette: {
        primary: { light: '#63A4FF', main: '#1F75FF', dark: '#004BB5', contrastText: '#fff' },
        secondary: { light: '#FF5CA2', main: '#E0015E', dark: '#B0004C', contrastText: '#fff' },
        divider: '#1f75ff14',
        success: { light: '#81C784', main: '#43A047', dark: '#388E3C', contrastText: '#fff' },
        warning: { light: '#FFB74D', main: '#FB8C00', dark: '#F57C00', contrastText: '#fff' },
        error: { light: '#E57373', main: '#E53935', dark: '#D32F2F', contrastText: '#fff' },
        info: { light: '#64B5F6', main: '#2196F3', dark: '#1976D2', contrastText: '#fff' },
        action: {
            active: 'rgba(31,117,255,0.54)',
            selected: 'rgba(31,117,255,0.06)',
            hover: 'rgba(31,117,255,0.045)',
            disabled: 'rgba(31,117,255,0.26)',
            disabledBackground: 'rgba(31,117,255,0.12)',
            focus: 'rgba(23, 32, 45, 0.12)',
        },
        text: { primary: '#13304B', secondary: '#566874' },
        background: { default: '#F7F8F9', paper: '#FFFFFF', neutral: '#ECF1F7', secondary: '#EFF5FC', },
    },
    typography: {
        fontSize: 15,
    },
    shape: {
        borderRadius: 12,
    },
    shadows,
    components: {
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                body: { backgroundColor: theme.palette.background.default },
            }),
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundImage: 'none',
                }),
            },
            variants: [
                {
                    props: { variant: 'section' },
                    style: ({ theme }) => ({
                        padding: '2rem clamp(16px, 5vw, 32px)',
                        marginBottom: '0.6rem',
                        boxShadow: theme.shadows[1],
                    }),
                },
            ],
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderColor: theme.palette.background.paper,
                }),
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                },
                h1: { fontSize: '1.5rem', fontWeight: 600, margin: '3rem 1rem 1.5rem', },
                h2: { fontSize: '1.35rem', fontWeight: 600, margin: '2.5rem 0 1.25rem', },
                h3: { fontSize: '1.25rem', fontWeight: 600, margin: '2rem 0 1rem', },
                h4: { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 0.75rem', },
                body1: { fontSize: '0.9rem', fontWeight: 400 },
                body2: { fontSize: '0.75rem', fontWeight: 400 },
            },
        },
        MuiInputLabel: {
            defaultProps: {
                shrink: true,
            },
        },
        MuiTextField: {
            defaultProps: {
                fullWidth: true,
                size: 'small',
                color: 'primary',
                variant: 'outlined',
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiInputLabel-root': {
                        color: theme.palette.text.primary,
                        backgroundColor: '#fff',
                        padding: '0 4px',
                        borderRadius: '4px',
                    },
                    '& .MuiOutlinedInput-root': {
                        fontSize: '0.9rem',
                        backgroundColor: theme.palette.background.secondary,
                        '& fieldset': {
                            borderColor: theme.palette.divider,
                        },
                        '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-disabled, &.MuiInputBase-readOnly': { // disabled と readOnly 両方
                            backgroundColor: '#fff', // 背景色
                            '& fieldset': {
                                borderColor: '#DDEAF7', // 枠線固定
                                color: 'rgba(0, 0, 0, 1)'
                            },
                            '& input, & textarea': {  // 実際の入力文字も固定
                                color: theme.palette.text.primary,
                                WebkitTextFillColor: theme.palette.text.primary, // Chrome 用
                            },
                            '&:hover fieldset': {
                                borderColor: '#DDEAF7', // hover 無効
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#DDEAF7', // focus 無効
                                borderWidth: '1px',
                            },
                        },
                    },
                    '& .MuiSelect-select.Mui-disabled': {
                        color: theme.palette.text.primary,
                        WebkitTextFillColor: theme.palette.text.primary, // Chrome 用
                    },
                }),
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                color: 'primary',
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: '#EFF5FC',
                    '& fieldset': {
                        borderColor: '#DDEAF7',
                    },
                }),
                icon: ({ theme }) => ({
                    color: theme.palette.primary.main,
                    '&.Mui-disabled': {
                        color: '#fff', // disabled時アイコン色
                    },
                }),
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: '4px 0',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 9999,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',
                },
                containedPrimary: ({ theme }) => ({
                    background: `linear-gradient(to right, #1CABF7, ${theme.palette.primary.main})`,
                    color: '#fff',

                    '&:hover': {
                        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
                        boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                        background: theme.palette.action.disabledBackground,
                    },

                }),
                outlinedPrimary: ({ theme }) => ({
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                        backgroundColor: `${theme.palette.primary.main}10`,
                    },
                }),
                sizeSmall: { padding: '4px 14px' },
                sizeMedium: { padding: '6px 20px' },
                sizeLarge: { padding: '10px 30px' },
            },
            variants: [
                {
                    props: { variant: 'related-menu' },
                    style: ({ theme }) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '9rem',
                        minHeight: '6rem',
                        whiteSpace: 'normal',
                        padding: '1.5rem 10px',
                        borderRadius: '1.2rem',
                        textAlign: 'center',
                        border: '1px solid rgba(39, 136, 255, 0.2)',
                        background: '#fff',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(39,136,255,0.15)',
                        '& .MuiSvgIcon-root, & .material-icons': {
                            marginBottom: 4,
                            fontSize: '2rem',
                        },
                        '&:hover': {
                            boxShadow: '0 4px 0 rgba(39,136,255,0)',
                            backgroundColor: '#fff'
                        },
                    }),
                },
            ],
        },
        MuiCheckbox: {
            defaultProps: {
                icon: <RadioButtonUnchecked sx={{ fontSize: 20 }} />,
                checkedIcon: <CheckCircle sx={{ fontSize: 20 }} />,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#CEDFF3',
                    '&.Mui-checked': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiSwitch: {
            defaultProps: {
                color: 'primary',
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '6px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #EAEFF5',
                },
                head: ({ theme }) => ({
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.paper,
                }),
            },
            variants: [
                {
                    props: { variant: 'labelCell' },
                    style: ({ theme }) => ({
                        padding: 16,
                        borderBottom: `solid 3px ${theme.palette.background.neutral}`,
                        fontWeight: 500,
                        width: '160px',
                        whiteSpace: 'nowrap',
                    }),
                },
            ],
        },
        MuiChip: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.primary,
                    minWidth: 30,
                }),
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 12,
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: 20,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    paddingTop: theme.spacing(3),
                    paddingBottom: theme.spacing(1),
                    paddingInline: '5%',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }),
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingInline: '5%',
                }),
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingInline: '5%',
                    paddingBottom: theme.spacing(4),
                    gap: 1,
                }),
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: ({ theme }) => ({
                    borderRight: 0,
                    background: theme.palette.background.neutral,
                }),
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    width: '100%',
                    fontSize: 13,
                },
            },
        },
        MuiSnackbar: {
            defaultProps: {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                },
            },
            styleOverrides: {
                root: {
                    width: '100%',
                    maxWidth: 500,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    fontSize: 13,
                },
            },
        },
    },
});

export default theme;
